import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {TooltipService} from 'app/core/services/tooltip.service';
import {Subscription} from 'rxjs';
import {audit} from 'rxjs/operators';

interface TooltipPosition {
    x: number;
    y: number;
    css: {
        [key: string]: string;
    };
}

const ARROW_HEIGHT = 10;
const PAGE_MARGIN = 10;
const TOOLTIP_PADDING = 7;

@Component({
    selector: 'tooltip-outlet',
    templateUrl: './tooltip-outlet.component.html',
    styleUrls: ['./tooltip-outlet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class TooltipOutletComponent implements OnInit, OnDestroy {
    @ViewChild('tooltipBody', {static: false}) $tooltipEl: ElementRef<HTMLDivElement>;
    ready = false;
    text: string;
    template: TemplateRef<any>;
    templateContext: any;
    private hiding: Promise<void>;
    private displayFrameRequest: number;
    private showSubscription: Subscription;
    private hideSubscription: Subscription;
    private sidePriority = {
        top: {
            checker: (targetRect: DOMRect, tooltipRect: DOMRect): boolean => targetRect.top >= tooltipRect.height + ARROW_HEIGHT,
            executor: (targetRect: DOMRect, tooltipRect: DOMRect): TooltipPosition => {
                let {x, dx, arrowDx} = this.getHorizontalPosition(targetRect, tooltipRect);
                return {
                    x,
                    y: Math.round(targetRect.top),
                    css: {
                        '--dx': `-${dx}px`,
                        '--dy': `-${Math.round(tooltipRect.height + ARROW_HEIGHT + TOOLTIP_PADDING)}px`,
                        '--arrow-dx': `${arrowDx}px`,
                    },
                };
            },
        },
        bottom: {
            /* istanbul ignore next */
            checker: (targetRect: DOMRect, tooltipRect: DOMRect): boolean =>
                window.innerHeight - targetRect.bottom >= tooltipRect.height + ARROW_HEIGHT,
            /* istanbul ignore next */
            executor: (targetRect: DOMRect, tooltipRect: DOMRect): TooltipPosition => {
                let {x, dx, arrowDx} = this.getHorizontalPosition(targetRect, tooltipRect);
                return {
                    x,
                    y: Math.round(targetRect.bottom),
                    css: {
                        '--dx': `-${dx}px`,
                        '--dy': `${ARROW_HEIGHT + TOOLTIP_PADDING}px`,
                        '--arrow-dx': `${arrowDx}px`,
                    },
                };
            },
        },
        right: {
            /* istanbul ignore next */
            checker: (targetRect: DOMRect, tooltipRect: DOMRect): boolean =>
                window.innerWidth - targetRect.right >= tooltipRect.width + ARROW_HEIGHT,
            /* istanbul ignore next */
            executor: (targetRect: DOMRect, tooltipRect: DOMRect): TooltipPosition => {
                let {y, dy, arrowDy} = this.getVerticalPosition(targetRect, tooltipRect);
                return {
                    x: Math.round(targetRect.right),
                    y,
                    css: {
                        '--dx': `${ARROW_HEIGHT}px`,
                        '--dy': `-${dy}px`,
                        '--arrow-dy': `${arrowDy}px`,
                    },
                };
            },
        },
        left: {
            /* istanbul ignore next */
            checker: (targetRect: DOMRect, tooltipRect: DOMRect): boolean => targetRect.left >= tooltipRect.width + ARROW_HEIGHT,
            /* istanbul ignore next */
            executor: (targetRect: DOMRect, tooltipRect: DOMRect): TooltipPosition => {
                let {y, dy, arrowDy} = this.getVerticalPosition(targetRect, tooltipRect);
                return {
                    x: Math.round(targetRect.left),
                    y,
                    css: {
                        '--dx': `-${Math.round(tooltipRect.width) + ARROW_HEIGHT}px`,
                        '--dy': `-${dy}px`,
                        '--arrow-dy': `${arrowDy}px`,
                    },
                };
            },
        },
    };

    constructor(
        private tooltipService: TooltipService,
        private $el: ElementRef,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.showSubscription = this.tooltipService.show$
            .pipe(
                audit(() => {
                    if (this.hiding) {
                        return this.hiding;
                    } else if (this.ready) {
                        return this.onHide();
                    } else {
                        return Promise.resolve();
                    }
                })
            )
            .subscribe(data => this.onShow(data.text, data.rect, data.context));
        this.hideSubscription = this.tooltipService.hide$.subscribe(() => this.onHide());
    }

    ngOnDestroy() {
        this.showSubscription.unsubscribe();
        this.hideSubscription.unsubscribe();
        cancelAnimationFrame(this.displayFrameRequest);
    }

    private getHorizontalPosition(targetRect: DOMRect, tooltipRect: DOMRect) {
        let x = Math.round(targetRect.left + targetRect.width / 2);
        let dx = Math.round(tooltipRect.width / 2);
        let leftEdge = x - dx;
        let rightEdge = x + dx;
        let arrowDx = 0;
        if (leftEdge < PAGE_MARGIN) {
            arrowDx = leftEdge - PAGE_MARGIN;
        }
        if (rightEdge > window.innerWidth - PAGE_MARGIN) {
            if (rightEdge - window.innerWidth - PAGE_MARGIN < dx) {
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                arrowDx = dx - targetRect.width / 2;
            } else {
                arrowDx = rightEdge - (window.innerWidth - PAGE_MARGIN);
            }
        }
        dx += arrowDx;
        return {x, dx, arrowDx};
    }

    /* istanbul ignore next */
    private getVerticalPosition(targetRect: DOMRect, tooltipRect: DOMRect) {
        let y = Math.round(targetRect.top + targetRect.height / 2);
        let dy = Math.round(tooltipRect.height / 2);
        let topEdge = y - dy;
        let bottomEdge = y + dy;
        let arrowDy = 0;
        if (topEdge < PAGE_MARGIN) {
            arrowDy = topEdge - PAGE_MARGIN;
        }
        if (bottomEdge > window.innerHeight - PAGE_MARGIN) {
            arrowDy = bottomEdge - (window.innerHeight - PAGE_MARGIN);
        }
        dy += arrowDy;
        return {y, dy, arrowDy};
    }

    private onShow(text: string | TemplateRef<any>, rect: DOMRect, context: any) {
        this.templateContext = null;
        if (text instanceof TemplateRef) {
            this.template = text;
        } else {
            this.text = text;
        }
        this.templateContext = context;
        this.ready = true;
        this.cd.detectChanges();
        this.displayFrameRequest = requestAnimationFrame(() => {
            this.setTooltipPosition(rect);
            this.displayFrameRequest = null;
        });
    }

    private setTooltipPosition(rect: DOMRect) {
        let {style} = this.$el.nativeElement;
        let tooltipEl = this.$tooltipEl.nativeElement;
        let tooltipRect = this.$tooltipEl.nativeElement.getBoundingClientRect() as DOMRect;
        let positionParams = this.validateSides(rect, tooltipRect);

        style.setProperty('--x', `${positionParams.position.x}px`);
        style.setProperty('--y', `${positionParams.position.y}px`);
        tooltipEl.classList.add('visible', positionParams.side);
        for (let [property, value] of Object.entries(positionParams.position.css)) {
            style.setProperty(property, value);
        }
    }

    private onHide(): Promise<void> {
        if (this.ready) {
            return (this.hiding = new Promise(async resolve => {
                cancelAnimationFrame(this.displayFrameRequest);
                if (this.$tooltipEl) {
                    await this.playHidingAnimation();
                }
                this.resetTooltip();
                setTimeout(() => resolve(), 0);
            }));
        }
        return Promise.resolve();
    }

    private resetTooltip() {
        this.text = null;
        this.template = null;
        this.ready = false;
        this.hiding = null;
        this.cd.detectChanges();
    }

    private playHidingAnimation() {
        return new Promise<void>(resolve => {
            let {style} = this.$el.nativeElement;
            let tooltipEl = this.$tooltipEl.nativeElement;
            let animationEndListener = () => {
                tooltipEl.removeEventListener('animationend', animationEndListener);
                tooltipEl.classList.remove(...tooltipEl.classList);
                style.setProperty('--x', '0px');
                style.setProperty('--y', '0px');
                style.setProperty('--dx', '0px');
                style.setProperty('--dy', '0px');
                style.setProperty('--arrow-dx', '0px');
                style.setProperty('--arrow-dy', '0px');
                resolve();
            };
            tooltipEl.addEventListener('animationend', animationEndListener);
            tooltipEl.classList.add('hiding');
        });
    }

    private validateSides(targetRect: DOMRect, tooltipRect: DOMRect): {side: string; position: TooltipPosition} {
        let [side, {executor}] = [...Object.entries(this.sidePriority)].find(([sideName, {checker}]) => checker(targetRect, tooltipRect));
        return {
            side,
            position: executor(targetRect, tooltipRect),
        };
    }
}
